import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ActiveCase,
  CASE_ROTATION_ORDER,
  CaseRotationStatus,
  NEW_CASES_ROTATION_GROUP,
  OFF_OCS_STATUSES,
} from '#entities';
import { useIsModalActive } from '#shared';

import { useQueryActiveCases } from '../api';
import {
  useSelectCarouselDuration,
  useSelectIsRotationOn,
} from '../widgetManager';

import { useIsIdle } from './useIsIdle';

type ReturnType = {
  activeRotationStatus: CaseRotationStatus | undefined;
};

const isCaseRotationStatus = (status: unknown): status is CaseRotationStatus =>
  CASE_ROTATION_ORDER.includes(status as CaseRotationStatus);

// compare data statuses to avoid restart every 60s (new data fetched)
const extractStatuses = (cases: ActiveCase[]): CaseRotationStatus[] => {
  const set = new Set<CaseRotationStatus>();
  for (const { status } of cases) {
    if (isCaseRotationStatus(status)) {
      set.add(status);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
};

export const useHighlightRotation = (opts?: {
  paused?: boolean;
}): ReturnType => {
  const rotationInterval = useSelectCarouselDuration();
  const isRotationOn = useSelectIsRotationOn();
  const { data: activeCases } = useQueryActiveCases();
  const isIdle = useIsIdle();
  const isModalActive = useIsModalActive();

  const [activeRotationStatus, setActiveRotationStatus] =
    useState<CaseRotationStatus>();

  const paused = !!opts?.paused;
  const shouldRotate = isRotationOn && isIdle && !paused && !isModalActive;
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const statusIndexRef = useRef(0);
  const previousStatusesRef = useRef<CaseRotationStatus[]>([]);

  // Collapse any present Off-OCS statuses into one rotation step.
  const nextStatuses = useMemo<CaseRotationStatus[]>(() => {
    if (!activeCases.length) {
      return [];
    }

    const present = extractStatuses(activeCases);

    // make NEW group "present" for filtering against CASE_ROTATION_ORDER
    const presentPlusNew: CaseRotationStatus[] = [
      NEW_CASES_ROTATION_GROUP,
      ...present,
    ];

    const hasAnyOffOcs = OFF_OCS_STATUSES.some(s => present.includes(s));

    const result: CaseRotationStatus[] = [];
    let offInserted = false;

    for (const s of CASE_ROTATION_ORDER) {
      if (!presentPlusNew.includes(s)) {
        continue;
      }

      if (OFF_OCS_STATUSES.includes(s)) {
        if (hasAnyOffOcs && !offInserted) {
          // insert a single representative once
          result.push(OFF_OCS_STATUSES[0]);
          offInserted = true;
        }
        // skip individual Off-OCS members
        continue;
      }

      result.push(s);
    }

    return result;
  }, [activeCases]);

  // keep stored statuses in sync without resetting every fetch
  useEffect(() => {
    const hasChanged =
      previousStatusesRef.current.length !== nextStatuses.length ||
      previousStatusesRef.current.some((s, i) => s !== nextStatuses[i]);

    if (hasChanged) {
      previousStatusesRef.current = nextStatuses;
      statusIndexRef.current = 0;

      // Only expose first status immediately if we are NOT rotating.
      // If we *are* rotating, the rotation effect will prime it.
      // Use queueMicrotask to avoid setState warning in effect
      if (!shouldRotate && nextStatuses.length > 0) {
        queueMicrotask(() => {
          setActiveRotationStatus(nextStatuses[0]);
        });
      }
    }
  }, [nextStatuses, shouldRotate]);

  // start/stop rotation and always start from first handling
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    const currentStatuses = previousStatusesRef.current;
    if (!currentStatuses.length) {
      return;
    }

    if (!shouldRotate) {
      // Reset to first when stopping
      statusIndexRef.current = 0;
      setActiveRotationStatus(currentStatuses[0]);
      return;
    }

    // When (re)starting: prime first and start interval
    statusIndexRef.current = 0;
    setActiveRotationStatus(currentStatuses[0]);

    timerRef.current = setInterval(() => {
      const statuses = previousStatusesRef.current;
      statusIndexRef.current = (statusIndexRef.current + 1) % statuses.length;
      setActiveRotationStatus(statuses[statusIndexRef.current]);
    }, rotationInterval);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [shouldRotate, nextStatuses, rotationInterval]);

  return {
    // expose a status only when rotating (idle), otherwise undefined
    activeRotationStatus: shouldRotate ? activeRotationStatus : undefined,
  };
};
